import { Box, CircularProgress, Typography } from '@mui/material';
import { useRef, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useHeaderActions from 'app/hooks/useHeaderActions';
import useHeaderTitle from 'app/hooks/useHeaderTitle';
import useNavigationHeader from 'app/hooks/useNavigationHeader';
import MarketingStationLayout from 'app/shared-components/MarketingStationLayout';
import ErrorState from 'app/shared-components/common/ErrorState';

import { BrandPaletteEditor, type BrandPalette } from '../../BrandPalette/BrandPaletteEditor';
import { useGetWebsiteTheme } from '../hooks/useGetWebsiteTheme';
import { useUpdateColorPalette } from './hooks/useUpdateColorPalette';

const BrandPaletteEditPage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const websiteId = searchParams.get('websiteId') ?? '';
  const organisationId = searchParams.get('organisationId') ?? '';

  const { theme, isLoading, isError, error } = useGetWebsiteTheme({ websiteId });
  const { mutate: updateColorPalette, isLoading: isSaving } = useUpdateColorPalette();

  const paletteRef = useRef<BrandPalette | null>(null);

  const themePath = `/plus-websites/theme-and-styles?${new URLSearchParams({ organisationId })}`;

  function handleSave() {
    const primary = paletteRef.current?.primary;
    if (!primary) return;
    updateColorPalette(
      { websiteId, primaryColor: primary },
      { onSuccess: () => navigate(themePath) }
    );
  }

  useNavigationHeader({ title: 'Back', navigateTo: themePath });
  useHeaderTitle('Brand palette');
  useHeaderActions([
    {
      title: 'Save',
      type: 'button',
      callback: handleSave,
      disabled: isSaving,
    },
  ]);

  if (!websiteId) {
    return (
      <ErrorState
        title="Missing required parameters"
        subtitle="Cannot edit the brand palette without a valid website."
      />
    );
  }

  if (isError) {
    return (
      <ErrorState
        title="Failed to load theme"
        subtitle={error instanceof Error ? error.message : 'Try again later'}
      />
    );
  }

  if (isLoading || !theme) {
    return (
      <MarketingStationLayout className="vtwo:flex vtwo:h-full vtwo:items-center vtwo:justify-center">
        <CircularProgress size={24} />
      </MarketingStationLayout>
    );
  }

  return (
    <MarketingStationLayout className="vtwo:flex vtwo:flex-col vtwo:h-full vtwo:overflow-hidden">
      <Box className="vtwo:flex-1 vtwo:bg-plus-canvas vtwo:overflow-y-auto">
        <Box className="vtwo:max-w-3xl vtwo:mx-auto vtwo:py-12 vtwo:px-8">
          <Box className="vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white vtwo:p-8">
            <Typography className="vtwo:text-lg vtwo:font-bold vtwo:text-typography-primary vtwo:mb-6">
              Brand palette
            </Typography>

            <BrandPaletteEditor
              initialColor={theme.colorPalette.primary}
              websiteId={websiteId}
              onChange={(palette) => {
                paletteRef.current = palette;
              }}
            />
          </Box>
        </Box>
      </Box>
    </MarketingStationLayout>
  );
};

export default BrandPaletteEditPage;
